import type {
  RosterCompResAllTimeCommitsRelatedResRelatedPagedData,
  SportFilter,
} from '@on3/api';
import { type ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { externalApi } from '@on3/ui-lib/index';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { primaryTextColor } from '@on3/ui-lib/utils/retrieveColorStyle';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getHighSchoolSportLabel } from '@on3/ui-lib/utils/url';
import { BreadCrumb } from '@public/components/BreadCrumb/BreadCrumb';
import { HighSchoolNav } from '@public/components/HighSchool/Nav/HighSchoolNav';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { type AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { FormerAthletesItem } from './FormerAthletesItem';
import styles from './FullFormerAthletesList.module.scss';

export const FullFormerAthletesList = ({
  allTimeCommits,
  title,
  filters,
}: {
  allTimeCommits?: RosterCompResAllTimeCommitsRelatedResRelatedPagedData;
  title: string;
  filters: {
    sports?: SportFilter[];
  };
}) => {
  const { relatedModel, pagination, list } = allTimeCommits ?? {};
  const { count, pageCount } = pagination || {};
  const { sport } = relatedModel || {};
  const [disable, setDisable] = useState(false);
  const [pageNumber, setPageNumber] = useState(2);
  const [commitList, setCommitList] = useState(list ?? []);

  const router = useRouter();
  const { sports } = filters;
  const { id } = router.query;
  const orgId = id?.toString().split('-').pop();
  const page = pageNumber;
  const showLoadMoreButton =
    count &&
    count > 50 &&
    (pageCount ?? 0) > pageNumber &&
    list &&
    list?.length < count;
  const sluggedSport = slugify(sport?.name || 'football');

  const handleChange: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    const params = {
      sportKey: sport?.key,
      page: (page && +page + 1) || 2,
    };

    try {
      const response = await externalApi.get(
        `/public/rdb/v2/organizations/${orgId}/all-time-commits?`,
        { params },
      );

      setCommitList([...commitList, ...response.data.list]);
      setPageNumber(pageNumber + 1);
      const responsePage = response.data.pagination?.currentPage ?? 2;

      setPageNumber(pageNumber + 1);

      router.replace(
        {
          query: { ...router.query, page: responsePage },
        },
        undefined,
        { shallow: true },
      );
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error('All Commits Error: ', response?.data?.message || message);
    } finally {
      setDisable(false);
    }
  }, [sport, page, orgId, commitList, pageNumber, router]);

  const { slug, name, key } = relatedModel?.organization || {};
  const urlSlug = `${slug}-${key}`;
  const schoolPrimaryColor = adjustColorForReadability(
    relatedModel?.organization?.primaryColor || '#fe3b1f',
  );

  const teamName = name || 'High School';

  return (
    <AdSidebarLayout>
      <BreadCrumb
        color={schoolPrimaryColor}
        crumbs={[
          { title: 'High School', href: '/high-school/' },
          { title: teamName, href: `/high-school/${urlSlug}/` },
        ]}
        title={`Top ${sport ? getHighSchoolSportLabel(sport) : ''} Alumni`}
      />
      <section className={styles.block}>
        <header className={styles.header}>
          <div className={styles.title}>
            <figure>
              <On3Avatar
                altText={title}
                domain={relatedModel?.organization?.defaultAsset?.domain}
                source={relatedModel?.organization?.defaultAsset?.source}
                teamAvatar
              />
            </figure>
            <h1>{title}</h1>
          </div>
          <nav
            className={styles.filterWrapper}
            style={{ color: schoolPrimaryColor }}
          >
            <Filter
              name="sport"
              navigate={(_, value) =>
                `/high-school/${urlSlug}/${value}/alumni/`
              }
              options={(sports || []).map((s) => ({
                label: getHighSchoolSportLabel(s),
                value: slugify(s.name),
              }))}
              value={sluggedSport}
            />

            <HighSchoolNav />
          </nav>
        </header>
        <ol className={styles.list}>
          {!!commitList &&
            commitList.map((item, index) => (
              <FormerAthletesItem
                athlete={item}
                index={index}
                key={item.hsPsoKey}
                relatedModel={relatedModel}
              />
            ))}
        </ol>
        {!!showLoadMoreButton && (
          <div>
            <button
              className={styles.btnLoadMore}
              disabled={disable}
              onClick={handleChange}
              style={primaryTextColor(schoolPrimaryColor)}
              type="button"
            >
              Load More
            </button>
          </div>
        )}
      </section>
    </AdSidebarLayout>
  );
};
